import { useEffect, useState } from 'react';
import ErrorDisplay from '../../../../components/ErrorDisplay';
import type { QuestTemplateFeedbackFirestore } from '../../services/questTypes';
import { QuestFormActions } from './QuestFormActions';
import { QuestFormFields } from './QuestFormFields';
import { QuestFormSectionDivider } from './QuestFormSectionDivider';
import { useQuestForm } from './useQuestForm';

interface FeedbackSpecificFormData {
    buttonText: string;
}

interface FeedbackQuestFormProps {
    questTemplate?: QuestTemplateFeedbackFirestore;
    initialOrder: number;
    onSave: (questTemplate: QuestTemplateFeedbackFirestore) => void;
    onSaveAndContinue?: (questTemplate: QuestTemplateFeedbackFirestore) => void;
    onCancel: () => void;
}

export const FeedbackQuestForm = ({ questTemplate, initialOrder, onSave, onSaveAndContinue, onCancel }: FeedbackQuestFormProps) => {
    const {
        commonFormData,
        loading,
        error,
        handleCommonChange,
        handleSubmit,
        handleSaveAndContinue
    } = useQuestForm({
        questTemplate,
        initialOrder,
        defaultProgressType: 'fixed'
    });

    const [specificFormData, setSpecificFormData] = useState<FeedbackSpecificFormData>({
        buttonText: 'Submit Feedback',
    });

    useEffect(() => {
        if (questTemplate && questTemplate.id) {
            setSpecificFormData({
                buttonText: questTemplate.buttonText,
            });
        }
    }, [questTemplate]);

    const handleSpecificChange = (field: string, value: string | boolean) => {
        setSpecificFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleChange = (field: string, value: string | boolean) => {
        if (field in commonFormData) {
            handleCommonChange(field as keyof typeof commonFormData, value as string | boolean);
        } else {
            handleSpecificChange(field, value);
        }
    };

    const validateSpecificFields = (): string | null => {
        if (!specificFormData.buttonText.trim()) {
            return 'Button Text is required for feedback quests';
        }
        return null;
    };

    const createQuestTemplate = (baseData: Partial<QuestTemplateFeedbackFirestore>): QuestTemplateFeedbackFirestore => {
        return {
            ...baseData,
            type: 'feedback',
            maxProgress: 1,
            buttonText: specificFormData.buttonText,
        } as QuestTemplateFeedbackFirestore;
    };

    const onSubmit = handleSubmit(validateSpecificFields, createQuestTemplate, onSave);
    const onSaveAndContinueClick = onSaveAndContinue ? handleSaveAndContinue(validateSpecificFields, createQuestTemplate, onSaveAndContinue) : undefined;

    return (
        <form onSubmit={onSubmit} className="space-y-4">
            {error && (
                <ErrorDisplay message={error} />
            )}

            <QuestFormFields
                formData={commonFormData}
                questType="feedback"
                disableProgressType={true}
                onChange={handleChange}
            />

            <QuestFormSectionDivider title="Feedback-specific fields" />

            {/* Feedback-specific fields */}
            <div>
                <label htmlFor="buttonText" className="block text-sm font-medium text-gray-700 mb-1">
                    Button Text *
                </label>
                <input
                    type="text"
                    id="buttonText"
                    value={specificFormData.buttonText}
                    onChange={(e) => handleChange('buttonText', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Submit Feedback"
                    required
                />
            </div>

            {error && (
                <ErrorDisplay message={error} />
            )}

            <QuestFormActions
                onCancel={onCancel}
                onSaveAndContinue={onSaveAndContinueClick}
                loading={loading}
                isEditMode={!!questTemplate?.id}
            />
        </form>
    );
};
